import {
  ARRANGEMENT_BOOKLETS,
  ARRANGEMENT_TYPE_ANY,
  ARRANGEMENT_TYPE_KEYS,
  ARRANGEMENT_TYPE_NONE,
  ARRANGEMENT_TYPES,
  type ArrangementBooklet,
  type ArrangementType,
  type ArrangementTypeFilter,
  type ArrangementTypeNarrowing,
} from '@pams/shared';
import { canonicalListFilters, type ArrangementListFilters } from '@/lib/arrangementListFilters';
import { LIST_FILTERS_COPY } from './copy';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE ARRANGEMENT LIST'S URL-BACKED FILTER KEYS — `useServerTable`'s `filterKeys` for this page.
 *
 *  ⚠️ [C5b §6] A MODULE CONSTANT RATHER THAN A LITERAL AT THE CALL SITE, FOR ONE SECOND READER: the
 *  search hint's "Show matches" writes `searchHintPatch(keys)` into the URL through `setFilters`, and
 *  a patch key that is not one of these would be written nowhere — nuqs only tracks the keys it was
 *  given — so the reset would silently clear nothing. `Arrangements.test.tsx` W5 holds every key of
 *  every patch to this list OR to the session list below.
 *
 *  ⚠️ [amended LIST-FILTERS] ONE OWNER PER KEY. `divisionOrgUnitId` LEFT this list for
 *  `ARRANGEMENT_SESSION_FILTER_KEYS`: it is "Adjust filters"' now, held per officer in the browser
 *  until sign-out (`hooks/useArrangementListFilters.ts`), because a URL is per page and per tab and
 *  the filter must follow the operator across both order lists and every tab. A key with TWO owners
 *  is a key whose two values disagree, and the request would then carry whichever was spread last.
 *  So a hand-typed `?divisionOrgUnitId=` is INERT — untracked, never sent, dropped from `urlSearch`
 *  and `?from=` — and nothing in `web/`, `e2e/` or `docs/` produced one: the search hint's reset was
 *  its only writer, and that is partitioned below.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const ARRANGEMENT_LIST_FILTER_KEYS = [
  'status',
  'policyId',
  'dateFrom',
  'dateTo',
  /*
   * ⚠️ [LIFECYCLE] `archived` WAS DEAD URL SURFACE — declared here since P3a with NO
   * control anywhere rendering it, so the Archive the API had always supported was
   * unreachable from the screen. `show` replaces it and can express the third value the
   * boolean never could: "archived only", which is the view an operator needs to FIND
   * the order they just withdrew.
   */
  'show',
  'kind',
  // [P4] `standingOrderId` rides the existing opaque-string filters — the round-the-clock
  // list's "Generated" link sets it together with `kind`, and no hook change was needed.
  'standingOrderId',
  /*
   * ⚠️ [C3a §2] CLIENT-ONLY, AND STRIPPED IN `queryFn`. It is here so the chosen view lives in
   * the URL and survives a refresh — which is what `filterKeys` is for — and it is removed before
   * the request, because `GET /arrangements` has never declared it and an undeclared key is
   * STRIPPED SILENTLY by the validation middleware. That silence is the defect [D4.4] records at
   * length for `show`.
   */
  'view',
  /*
   * ⚠️ [amended P7B.1 §4] `type` WAS HERE — P7B's typed TAB — AND IS GONE. The type is the ROUTE now
   * (`/pa/:typeKey`, the list's `fixedType`), so it is never read from the URL; an old shared
   * `/arrangements?type=…` link is redirected to the type's own address before this list renders.
   */
] as const;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [LIST-FILTERS] THE SESSION STORE'S REQUEST KEYS — "Adjust filters", per officer, until sign-out.
 *
 *  ⚠️ `sessionFilterQuery` IS THE ONE MAPPING FROM THE STORED VALUE TO THE REQUEST, and BOTH the query
 *  key and the `queryFn` read it — so the key a page is cached under and the request that fills it
 *  cannot disagree. It answers `{}` with nothing applied, so every no-filter key and request is
 *  byte-identical to what it was before the filter existed.
 *
 *  ⚠️ A NEW FILTER IS FOUR STEPS, AND EACH ONE FAILS SILENTLY WHEN IT IS SKIPPED:
 *    1. a field on `ArrangementListFilters` and its canonicaliser (`lib/arrangementListFilters.ts`) —
 *       the stored form, so two selections meaning the same thing are one key and one request;
 *    2. a section in `ArrangementFiltersModal`'s `FILTER_SECTIONS` — the control;
 *    3. its entry in `sessionFilterQuery` below — or it is stored, shown as applied, and never sent;
 *    4. if the search hint can name it, its key here and its branch in `relaxSessionFilters` — or
 *       "Show matches" names the filter and clears nothing, because the URL does not hold it.
 *
 *  ⚠️ THE COST, STATED: "Show matches" naming a session filter clears it on BOTH order lists, in
 *  every tab — the store is one value, and a hint that relaxed it for one page only would be a
 *  second value nobody can see.
 *
 *  ⚠️ [TYPE-FILTER] "ORDER TYPE" TOOK STEPS 1–3 AND, DELIBERATELY, NOT 4. The server never names `type`
 *  in a hint — on the Durga Puja PA list it is the route, which a hint cannot relax — so it is not in
 *  the list below, and the empty state names a narrowing type itself ("Show all types"). It is sent
 *  from Manage Arrangements only; on a typed list the route's own type wins and the stored one is held.
 *
 *  ⚠️ [DP2026] "BOOKLET" IS ORDER TYPE'S MIRROR: steps 1–3, not 4, and sent from a TYPED list only. The server
 *  never names it in a hint, so a search it emptied says "with these filters", which stays true.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const ARRANGEMENT_SESSION_FILTER_KEYS = ['divisionOrgUnitId'] as const;
export type ArrangementSessionFilterKey = (typeof ARRANGEMENT_SESSION_FILTER_KEYS)[number];

const isSessionFilterKey = (key: string): key is ArrangementSessionFilterKey =>
  (ARRANGEMENT_SESSION_FILTER_KEYS as readonly string[]).includes(key);

/** What the stored filter adds to one list's request — and what a page records it was SENT with. */
export type SessionFilterQuery = {
  readonly divisionOrgUnitId?: readonly string[];
  readonly type?: ArrangementTypeNarrowing;
  /** [DP2026] A typed list's booklet narrowing — never `all`, which is no key at all. */
  readonly booklet?: Exclude<ArrangementBooklet, 'all'>;
};

/**
 * What the stored filter adds to `GET /arrangements` — the divisions as a LIST, which client-core's
 * `buildUrl` sends as repeated keys, and a narrowing "Order type". `{}` when nothing is applied.
 *
 * ⚠️ [TYPE-FILTER] CONDITIONAL SPREADS ONLY — never a key holding `undefined`. Spread last over the
 * request's own `type: 'any'`, an `undefined` would be dropped by `buildUrl`, the server would default
 * to `none`, and regular orders would be cached under the All key. On a typed list (`fixedType`) the
 * type is never added: the route's own wins.
 */
export const sessionFilterQuery = (
  filters: ArrangementListFilters,
  fixedType?: ArrangementType,
): SessionFilterQuery => ({
  ...(filters.divisionOrgUnitIds.length > 0 ? { divisionOrgUnitId: filters.divisionOrgUnitIds } : {}),
  ...(fixedType === undefined && filters.type !== ARRANGEMENT_TYPE_ANY ? { type: filters.type } : {}),
  /* [DP2026] The booklet is the typed list's own, so only a typed list sends it — and only when it narrows. */
  ...(fixedType !== undefined && filters.booklet !== 'all' ? { booklet: filters.booklet } : {}),
});

/**
 * Splits `searchHintPatch(keys)` between its two owners: the URL keys go to `table.setFilters`, the
 * session keys to the store. A key in neither list goes with the URL, which is where it always went —
 * and W5 is what keeps that set empty.
 */
export const partitionSearchHintPatch = (
  patch: Readonly<Record<string, string | undefined>>,
): { url: Record<string, string | undefined>; session: ArrangementSessionFilterKey[] } => {
  const url: Record<string, string | undefined> = {};
  const session: ArrangementSessionFilterKey[] = [];
  for (const [key, value] of Object.entries(patch)) {
    if (isSessionFilterKey(key)) session.push(key);
    else url[key] = value;
  }
  return { url, session };
};

/**
 * The stored filter with the named keys relaxed — the session half of "Show matches". Canonical, so a
 * filter relaxed to nothing is THE empty value and the store removes its key.
 */
export const relaxSessionFilters = (
  filters: ArrangementListFilters,
  keys: readonly ArrangementSessionFilterKey[],
): ArrangementListFilters =>
  canonicalListFilters({
    ...filters,
    divisionOrgUnitIds: keys.includes('divisionOrgUnitId') ? [] : filters.divisionOrgUnitIds,
  });

/**
 * [TYPE-FILTER] "Order type"'s choices, in the order the dialog lists them: every type, regular orders
 * only, then one per registered type — read from the registry, so next year's set is a radio for free.
 */
export const ORDER_TYPE_CHOICES: readonly { readonly value: ArrangementTypeFilter; readonly label: string }[] = [
  { value: ARRANGEMENT_TYPE_ANY, label: LIST_FILTERS_COPY.typeAll },
  { value: ARRANGEMENT_TYPE_NONE, label: LIST_FILTERS_COPY.typeNone },
  ...ARRANGEMENT_TYPE_KEYS.map((key) => ({
    value: key,
    label: LIST_FILTERS_COPY.typeOnly(ARRANGEMENT_TYPES[key].label),
  })),
];

/** A choice's words — "Regular PA only". */
/** [DP2026] The Booklet choices, in order, for one typed list — the year is the type's own. */
export const bookletChoices = (
  type: ArrangementType,
): readonly { readonly value: ArrangementBooklet; readonly label: string }[] =>
  ARRANGEMENT_BOOKLETS.map((value) => ({
    value,
    label:
      value === 'all'
        ? LIST_FILTERS_COPY.bookletAll
        : value === 'current'
          ? LIST_FILTERS_COPY.bookletCurrent(ARRANGEMENT_TYPES[type].year)
          : LIST_FILTERS_COPY.bookletEarlier,
  }));

export const orderTypeLabel = (type: ArrangementTypeFilter): string =>
  ORDER_TYPE_CHOICES.find((choice) => choice.value === type)?.label ?? LIST_FILTERS_COPY.typeAll;

/** A registered type's own label — "Durga Puja PA". */
const orderTypeLabelOf = (type: ArrangementType): string => ARRANGEMENT_TYPES[type].label;

/**
 * [TYPE-FILTER] What a filtered empty page says it hid — one sentence per filter the request SENT, so a
 * type-only filter never reads as a division one.
 */
export const sessionFilterNotes = (sent: SessionFilterQuery, fixedType: ArrangementType | undefined): string[] => [
  ...(sent.divisionOrgUnitId === undefined ? [] : [LIST_FILTERS_COPY.emptyNote]),
  ...(sent.type === undefined
    ? []
    : [
        sent.type === ARRANGEMENT_TYPE_NONE
          ? LIST_FILTERS_COPY.typeNoneNote
          : LIST_FILTERS_COPY.typeOnlyNote(orderTypeLabelOf(sent.type)),
      ]),
  ...(sent.booklet === undefined || fixedType === undefined
    ? []
    : [
        sent.booklet === 'current'
          ? LIST_FILTERS_COPY.bookletCurrentNote(ARRANGEMENT_TYPES[fixedType].year)
          : LIST_FILTERS_COPY.bookletEarlierNote,
      ]),
];

const divisionSummary = (
  ids: readonly string[],
  resolve: ((id: string) => { name: string } | undefined) | undefined,
): string | undefined => {
  if (ids.length === 0) return undefined;
  if (resolve === undefined) return LIST_FILTERS_COPY.summary([LIST_FILTERS_COPY.chosen(ids.length)]);
  const names = ids.flatMap((id) => {
    const known = resolve(id);
    return known ? [known.name] : [];
  });
  const missing = ids.length - names.length;
  return LIST_FILTERS_COPY.summary(missing > 0 ? [...names, LIST_FILTERS_COPY.unavailable(missing)] : names);
};

/**
 * ⚠️ THE BUTTON'S DESCRIPTION — `undefined` with nothing applied, and NEVER AN ID.
 *
 * `resolve` is the division tree's name lookup, passed only once that tree has ANSWERED. Until then the
 * line is a count ("2 chosen"); after, every id it cannot name is folded into "N no longer available"
 * rather than printed — `lib/selectOptions.ts`' rule for a value with no label, for a native `title`.
 */
export const listFiltersSummary = (
  filters: ArrangementListFilters,
  resolve: ((id: string) => { name: string } | undefined) | undefined,
  fixedType?: ArrangementType,
): string | undefined => {
  const lines = [divisionSummary(filters.divisionOrgUnitIds, resolve)].filter((line) => line !== undefined);
  /* [TYPE-FILTER] One line per filter THIS list honours — on a typed list the stored type is not one. */
  if (fixedType === undefined && filters.type !== ARRANGEMENT_TYPE_ANY) {
    lines.push(LIST_FILTERS_COPY.typeSummary(orderTypeLabel(filters.type)));
  }
  /* [DP2026] …and on a typed list, the booklet. */
  if (fixedType !== undefined && filters.booklet !== 'all') {
    const choice = bookletChoices(fixedType).find((entry) => entry.value === filters.booklet);
    if (choice) lines.push(LIST_FILTERS_COPY.bookletSummary(choice.label));
  }
  return lines.length === 0 ? undefined : lines.join(LIST_FILTERS_COPY.summarySeparator);
};
